import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import type { ApolloError } from 'apollo-client';

import config from 'js/app/config';

import { filterUndefined } from 'bundles/common/utils/commonUtils';
import type { AuthorProfile } from 'bundles/shared-articles/components/ArticleAuthorProfile';
import type {
  AuthoredArticleFieldsFragment as Article,
  ArticlesAuthorPageQuery,
  ArticlesAuthorPageQueryVariables,
} from 'bundles/unified-seo-content-common/__generated__/contentfulTypes';
import { articlesAuthor } from 'bundles/unified-seo-content-common/queries/AuthorPageQuery';

type InnerProps = {
  slug: string;
  articlesLimit?: number;
  articlesSkip?: number;
  preview?: boolean;
  skipArticles: number;
  numberOfArticles: number;
};

export type PageDataProps = {
  isPageLoading: boolean;
  error?: ApolloError;
  loading: boolean;
  author: AuthorProfile | undefined;
  articles: Array<Article>;
  articlesCount: number;
};

export const useArticlesAuthorPageQuery = ({
  slug,
  preview = false,
  skipArticles = 0,
  numberOfArticles,
}: InnerProps): PageDataProps => {
  const { data, loading, error } = useQuery<ArticlesAuthorPageQuery, ArticlesAuthorPageQueryVariables>(articlesAuthor, {
    variables: {
      slug,
      preview,
      limit: numberOfArticles,
      skip: skipArticles,
    },
    context: { clientName: preview ? 'contentfulPreviewGql' : 'contentfulGql' },
    errorPolicy: 'all',
    onError: (errorInfo) => {
      Sentry.captureException(errorInfo, {
        extra: { message: 'Error fetching articles author page data' },
      });
    },
  });

  const authorData = data?.articlesAuthorCollection?.items[0] || undefined;
  const articles = data?.articleCollection?.items.filter((article): article is Article => Boolean(article)) || [];
  const articlesCount = data?.articleCollection?.total || 0;

  let author: AuthorProfile | undefined;
  const { name, description, jobTitle, image: avatar, externalWebsites, knowsAboutCollection } = authorData || {};

  if (authorData) {
    author = {
      name,
      description,
      jobTitle,
      externalWebsites: externalWebsites?.filter((url): url is string => Boolean(url)) || undefined,
      knowsAbout: knowsAboutCollection?.items
        ?.map((knowsAbout) => {
          const { title, slug, languageCode, countryCode } = knowsAbout || {};
          if (title && slug && languageCode && countryCode) {
            return {
              slug,
              title,
              locale: {
                language: languageCode,
                country: countryCode,
              },
            };
          } else {
            return undefined;
          }
        })
        .filter(filterUndefined),
      image: avatar?.url ? `${config.url.imageProxyRoot}${avatar.url}` : undefined,
    };
  }

  return {
    isPageLoading: loading,
    error,
    loading,
    author,
    articles,
    articlesCount,
  };
};
